import React, { useEffect, useState } from 'react';
import { DragDropContext, DropResult, DragStart, BeforeCapture } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { Typography } from '@inplayer-org/inplayer-ui';

// types
import { AllAssets, PackageAssets, PackageAssetDetails, BulkAssetType } from 'ducks/merchantDucks/packages/types';
import { LoadPackageAssetsPayload } from 'ducks/merchantDucks/packages/actions';
import { ModalNames } from 'ducks/merchantDucks/modal/types';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// constants
import Rules from 'restrictions/roles/constants/rules';

// components
import ConfirmModal from 'components/Modals/ConfirmModal';
import AccordionFooter from 'components/AccordionFooter';
import { Optional } from 'utils/types';
import { transformSearchParams } from 'utils/helpers';
import DroppableColumn from './DroppableColumn';
import { PackagesSectionType, AccordionHelper } from '../../components/AccordionHelper';

const DragDropContainer = styled.div<{ isExpanded: boolean }>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  height: ${({ isExpanded }) => (isExpanded ? '105rem' : '83rem')};
  padding: 0 6% 2rem 6%;
`;

const Description = styled(Typography)`
  padding: 2rem 6% 0 6%;
`;

export enum DroppableNames {
  available = 'Available',
  added = 'Added',
}

enum ActionType {
  add = 'add',
  delete = 'delete',
}

interface SearchParams {
  id: Optional<number>;
  title: string;
}

interface OwnProps {
  packageId: number;
  loadPackageAssets: ({ id, page, excluded, limit }: LoadPackageAssetsPayload) => void;
  loadAssetExcludedFromPackage: (
    { id, page, excluded, limit }: LoadPackageAssetsPayload,
    deletePreviousAssets?: boolean
  ) => void;
  allAssets: AllAssets;
  packageAssets: PackageAssets;
  removeItemsFromPackage: (packageID: number) => void;
  totalPackageAssets: number;
  totalAddedPackageAssets: number;
  openModal: (modalName: ModalNames) => void;
  saveBulkPackageAssets: (packageId: number, addAssetIds: BulkAssetType, removeAssetIds: BulkAssetType) => void;
  closePanel: () => void;
  addAllAssetsToPackage: (packageID: number) => void;
  setShouldCloseSection: (value: boolean) => void;
  accordionLabel: string;
}

type Props = OwnProps;

const ManageAssets = ({
  loadPackageAssets,
  loadAssetExcludedFromPackage,
  packageId,
  allAssets: { isFetching: isAllAssetsFetching, collection: allAssetsCollection },
  packageAssets: { isFetching: isAddedAssetsFetching, collection: addedAssetsCollection },
  removeItemsFromPackage,
  totalPackageAssets,
  openModal,
  saveBulkPackageAssets,
  totalAddedPackageAssets,
  closePanel,
  addAllAssetsToPackage,
  setShouldCloseSection,
  accordionLabel,
}: Props) => {
  const { hasRule } = useRestrictionRules();
  const initialSearchParam = { id: null, title: '' };
  // pagination
  const [availableAssetsPage, setAvailableAssetsPage] = useState(1);
  const [addedAssetsPage, setAddedAssetsPage] = useState(1);

  // drag and drop assets
  const [availableAssets, setAvailableAssets] = useState(allAssetsCollection);
  const [addedAssets, setAddedAssets] = useState(addedAssetsCollection);

  // queued assets
  const [queueAdded, setQueueAdded] = useState<Array<PackageAssetDetails>>([]);
  const [queueAvailable, setQueueAvailable] = useState<Array<PackageAssetDetails>>([]);
  const [shouldAppendQueued, setShouldAppendQueued] = useState(true);

  const [droppableIdState, setDroppableId] = useState<string>();
  const [draggedItemId, setDraggedItemId] = useState<string>();

  // bulk state
  const [isAllAvailableChecked, checkAllAvailable] = useState(false);
  const [isAllAddedChecked, checkAllAdded] = useState(false);
  const [checkedAvailableAssets, setCheckedAvailableAssets] = useState({});
  const [checkedAddedAssets, setCheckedAddedAssets] = useState({});

  // search
  const [availableAssetsTags, setAvailableAssetsTags] = useState<SearchParams>(initialSearchParam);
  const [addedAssetsTags, setAddedAssetsTags] = useState<SearchParams>(initialSearchParam);

  const [actionType, setActionType] = useState<ActionType>();

  useEffect(() => {
    loadPackageAssets({ id: packageId });
    loadAssetExcludedFromPackage({ id: packageId });
  }, [loadPackageAssets, loadAssetExcludedFromPackage]);

  useEffect(() => {
    setShouldCloseSection(!queueAvailable.length && !queueAdded.length);
  }, [queueAdded.length, queueAvailable.length]);

  useEffect(() => {
    const assets = allAssetsCollection.filter((asset) => !queueAdded.some((x) => x.id === asset.id));
    if (shouldAppendQueued) {
      setAvailableAssets([...new Set([...queueAvailable, ...assets])]);
    } else {
      setAvailableAssets(assets);
    }
  }, [allAssetsCollection]);

  useEffect(() => {
    const assets = addedAssetsCollection.filter((asset) => !queueAvailable.some((x) => x.id === asset.id));
    if (shouldAppendQueued) {
      setAddedAssets([...new Set([...queueAdded, ...assets])]);
    } else {
      setAddedAssets(assets);
    }
  }, [addedAssetsCollection]);

  const toggleCheckbox = (droppableId: string) => (id: number) => () => {
    if (droppableId === DroppableNames.added) {
      setCheckedAddedAssets((prevCheckedAddedAssets) => {
        return { ...prevCheckedAddedAssets, [id]: !checkedAddedAssets[id] };
      });
    } else if (droppableId === DroppableNames.available) {
      setCheckedAvailableAssets((prevCheckedAvailableAssets) => {
        return {
          ...prevCheckedAvailableAssets,
          [id]: !checkedAvailableAssets[id],
        };
      });
    }
  };

  const onCheckAllAssets = (droppableId: string) => (isChecked: boolean): void => {
    if (droppableId === DroppableNames.added) {
      checkAllAdded(isChecked);

      const checked = addedAssets.reduce(
        (obj, e) => ({
          ...obj,
          [e.id]: isChecked,
        }),
        {}
      );
      setCheckedAddedAssets(checked);
    } else if (droppableId === DroppableNames.available) {
      checkAllAvailable(isChecked);

      const checked = availableAssets.reduce(
        (obj, e) => ({
          ...obj,
          [e.id]: isChecked,
        }),
        {}
      );
      setCheckedAvailableAssets(checked);
    }
  };

  const resetCheckOption = (droppableId?: string) => {
    if (isAllAvailableChecked || droppableId === DroppableNames.available) {
      checkAllAvailable(false);
      setCheckedAvailableAssets({});
    } else if (isAllAddedChecked || droppableId === DroppableNames.added) {
      checkAllAdded(false);
      setCheckedAddedAssets({});
    }
  };

  const handleLoadMore = (droppableId: string) => {
    setShouldAppendQueued(true);
    if (droppableId === DroppableNames.available) {
      const transformedParams = transformSearchParams(availableAssetsTags);
      loadAssetExcludedFromPackage({ id: packageId, page: availableAssetsPage + 1, search: transformedParams });
      setAvailableAssetsPage(availableAssetsPage + 1);
      resetCheckOption();
    } else {
      const transformedParams = transformSearchParams(addedAssetsTags);
      loadPackageAssets({
        id: packageId,
        page: addedAssetsPage + 1,
        search: transformedParams,
      });
      setAddedAssetsPage(addedAssetsPage + 1);
      resetCheckOption();
    }
  };

  const searchAction = (actionTags: SearchParams, searchId: string) => {
    const transformedParams = transformSearchParams(actionTags);
    if (searchId === DroppableNames.available) {
      loadAssetExcludedFromPackage({
        id: packageId,
        search: transformedParams,
      });
    } else {
      loadPackageAssets({ id: packageId, search: transformedParams });
    }
  };

  const handleAddition = (droppableId: string) => (searchParams: SearchParams) => {
    const isAvailableContainer = droppableId === DroppableNames.available;
    if (isAvailableContainer) {
      setAvailableAssetsTags(searchParams);
      setAvailableAssetsPage(1);
      resetCheckOption(DroppableNames.available);
    } else {
      setAddedAssetsTags(searchParams);
      setAddedAssetsPage(1);
      resetCheckOption(DroppableNames.added);
    }
    const isEmptySearch = Object.keys(searchParams).filter((key) => searchParams[key]).length === 0;
    if (isEmptySearch) {
      setShouldAppendQueued(true);
    } else {
      setShouldAppendQueued(false);
    }
    searchAction(searchParams, droppableId);
  };

  const handleOpenModal = (type: ActionType) => () => {
    setActionType(type);
    openModal(ModalNames.RemoveItemsFromPackage);
  };

  const handleCloseModal = () => {
    if (actionType === ActionType.delete) {
      removeItemsFromPackage(packageId);
    } else {
      addAllAssetsToPackage(packageId);
    }
    setAddedAssets([]);
    setQueueAdded([]);
    setAvailableAssets([]);
    setQueueAvailable([]);
    checkAllAdded(false);
    checkAllAvailable(false);
    setCheckedAddedAssets({});
    setCheckedAvailableAssets({});
    setAvailableAssetsTags(initialSearchParam);
    setAddedAssetsTags(initialSearchParam);
  };

  const handleDragStart = (initial: DragStart) => {
    const {
      source: { droppableId: id },
    } = initial;

    setDroppableId(id);
  };

  const handleBeforeCapture = (initial: BeforeCapture) => {
    const { draggableId: id } = initial;

    setDraggedItemId(id);
  };

  const clearAvailableFilter = (filter: SearchParams, appendQueued = false) => {
    setAvailableAssetsTags(filter);
    setShouldAppendQueued(appendQueued);
    setAvailableAssetsPage(1);
  };

  const clearAddedFilter = (filter: SearchParams, appendQueued = false) => {
    setAddedAssetsTags(filter);
    setShouldAppendQueued(appendQueued);
    setAddedAssetsPage(1);
  };

  const addItemsInQueue = (droppableId: string, draggedItems: Array<PackageAssetDetails>) => {
    const isAddItem = droppableId === DroppableNames.added;
    const initialCollection = isAddItem ? addedAssetsCollection : allAssetsCollection;

    const filteredDraggedItems = draggedItems.filter((x) => !initialCollection.some((y) => x.id === y.id));

    const addToQueueAdded = isAddItem
      ? [...filteredDraggedItems, ...queueAdded]
      : queueAdded.filter((item) => !draggedItems.some((x) => x.id === item.id));

    const addToQueueAvailable = isAddItem
      ? queueAvailable.filter((item) => !draggedItems.some((x) => x.id === item.id))
      : [...filteredDraggedItems, ...queueAvailable];

    setQueueAdded(addToQueueAdded);
    setQueueAvailable(addToQueueAvailable);
  };

  const handleSearchReset = (droppableId: string, itemsLength: number) => {
    const isEmptyAddedSearch = Object.keys(addedAssetsTags).filter((key) => addedAssetsTags[key]).length === 0;
    const isEmptyAvailableSearch =
      Object.keys(availableAssetsTags).filter((key) => availableAssetsTags[key]).length === 0;
    if (droppableId === DroppableNames.added) {
      if (!isEmptyAddedSearch) {
        clearAddedFilter(initialSearchParam, true);
        loadPackageAssets({ id: packageId });
      }
      if (itemsLength === 0 && availableAssetsPage * 15 < totalPackageAssets) {
        handleLoadMore(DroppableNames.available);
      }
    } else {
      if (!isEmptyAvailableSearch) {
        clearAvailableFilter(initialSearchParam, true);
        loadAssetExcludedFromPackage({ id: packageId });
      }
      if (itemsLength === 0 && addedAssetsPage * 15 < totalAddedPackageAssets) {
        handleLoadMore(DroppableNames.added);
      }
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;
    setDraggedItemId('');
    if (!destination) {
      return;
    }
    if (source.droppableId !== destination.droppableId) {
      let draggedItem: PackageAssetDetails;
      let draggedItems: Array<PackageAssetDetails> = [];
      if (destination.droppableId === DroppableNames.added) {
        draggedItem = availableAssets.find((item) => item.id === Number(draggableId)) as PackageAssetDetails;
        if (checkedAvailableAssets[draggedItem.id]) {
          draggedItems = availableAssets.filter((x) => checkedAvailableAssets[x.id]);
          resetCheckOption(DroppableNames.available);
        } else {
          draggedItems = [draggedItem];
        }
        const filteredAvailableAssets = availableAssets.filter((item) => !draggedItems.some((x) => x.id === item.id));
        setAvailableAssets(filteredAvailableAssets);
        setAddedAssets([...draggedItems, ...addedAssets]);
        addItemsInQueue(DroppableNames.added, draggedItems);
        handleSearchReset(DroppableNames.added, filteredAvailableAssets.length);
      } else if (destination.droppableId === DroppableNames.available) {
        draggedItem = addedAssets.find((item) => item.id === Number(draggableId)) as PackageAssetDetails;
        if (checkedAddedAssets[draggedItem.id]) {
          draggedItems = addedAssets.filter((x) => checkedAddedAssets[x.id]);
          resetCheckOption(DroppableNames.added);
        } else {
          draggedItems = [draggedItem];
        }
        const filteredAddedAssets = addedAssets.filter((item) => !draggedItems.some((x) => x.id === item.id));
        setAvailableAssets([...draggedItems, ...availableAssets]);
        setAddedAssets(filteredAddedAssets);
        addItemsInQueue(DroppableNames.available, draggedItems);
        handleSearchReset(DroppableNames.available, filteredAddedAssets.length);
      }
    }
  };

  const resetContainers = async () => {
    setShouldAppendQueued(false);
    await loadPackageAssets({ id: packageId });
    await loadAssetExcludedFromPackage({ id: packageId });
    setAddedAssetsPage(1);
    setAvailableAssetsPage(1);
    setAddedAssetsTags(initialSearchParam);
    setAvailableAssetsTags(initialSearchParam);
    setQueueAdded([]);
    setQueueAvailable([]);
  };

  const onFormSubmit = async () => {
    const filteredAddAssetIds = queueAdded.map((item) => item.id);
    const filteredRemoveAssetIds = queueAvailable.map((item) => item.id);

    await saveBulkPackageAssets(packageId, { ...filteredAddAssetIds }, { ...filteredRemoveAssetIds });
    resetContainers(); // Fix later bug when removing this call
    closePanel();
  };

  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);
  const isButtonDisabled = !queueAdded.length && !queueAvailable.length;

  // total assets for disabled load more button
  const totalAddedAssets = totalAddedPackageAssets + queueAdded.length - queueAvailable.length;
  const totalAvailableAssets = totalPackageAssets + queueAvailable.length - queueAdded.length;

  const isContainerExpanded =
    Object.keys(addedAssetsTags).filter((key) => addedAssetsTags[key]).length !== 0 ||
    Object.keys(availableAssetsTags).filter((key) => availableAssetsTags[key]).length !== 0;

  return (
    <>
      <AccordionHelper closePanel={closePanel} index={PackagesSectionType.ManageAssets} submitForm={onFormSubmit} />
      <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} onBeforeCapture={handleBeforeCapture}>
        <Description variant="p">
          Drag and drop assets to add or remove them from the package. Make sure to click Save to after setting
          everything up.
        </Description>
        <DragDropContainer isExpanded={isContainerExpanded}>
          <DroppableColumn
            title={DroppableNames.available}
            items={availableAssets}
            droppableId={DroppableNames.available}
            isDragDisabled={isReadOnly}
            buttonText="Load more"
            buttonAction={handleLoadMore}
            isLoaderVisible={isAllAssetsFetching}
            totalPackageAssets={totalAvailableAssets}
            handleAddition={handleAddition(DroppableNames.available)}
            filter={availableAssetsTags}
            clearFilter={clearAvailableFilter}
            showCustomButton={!isReadOnly}
            customButtonText="Add all assets"
            customButtonAction={handleOpenModal(ActionType.add)}
            isDroppableDisabled={isReadOnly || droppableIdState === DroppableNames.available}
            isCheckedAll={isAllAvailableChecked}
            toggleCheckbox={toggleCheckbox(DroppableNames.available)}
            checkedAssets={checkedAvailableAssets}
            onCheckAllAssets={onCheckAllAssets(DroppableNames.available)}
            isCheckboxDisabled={!availableAssets.length}
            assetsAmount={queueAdded.length}
            draggedItemId={draggedItemId}
          />
          <DroppableColumn
            title={`${DroppableNames.added} to package`}
            items={addedAssets}
            droppableId={DroppableNames.added}
            isDragDisabled={isReadOnly}
            isLoaderVisible={isAddedAssetsFetching}
            isReadOnly={isReadOnly}
            handleAddition={handleAddition(DroppableNames.added)}
            filter={addedAssetsTags}
            clearFilter={clearAddedFilter}
            showCustomButton={!isReadOnly}
            customButtonText="Remove all assets"
            customButtonAction={handleOpenModal(ActionType.delete)}
            totalPackageAssets={totalAddedAssets}
            isDroppableDisabled={isReadOnly || droppableIdState === DroppableNames.added}
            isCheckedAll={isAllAddedChecked}
            toggleCheckbox={toggleCheckbox(DroppableNames.added)}
            checkedAssets={checkedAddedAssets}
            onCheckAllAssets={onCheckAllAssets(DroppableNames.added)}
            isCheckboxDisabled={!addedAssets.length}
            assetsAmount={queueAvailable.length}
            draggedItemId={draggedItemId}
            buttonText="Load more"
            buttonAction={handleLoadMore}
          />
        </DragDropContainer>
      </DragDropContext>
      <ConfirmModal
        modalName={ModalNames.RemoveItemsFromPackage}
        message={`Are you sure you want to ${actionType} all assets ${
          actionType === ActionType.add ? 'to' : 'from'
        } this package?`}
        handleConfirm={handleCloseModal}
        buttonText={actionType}
      />
      <AccordionFooter
        accordionLabel={accordionLabel}
        onFormSubmit={onFormSubmit}
        handleCancel={closePanel}
        disabled={isButtonDisabled}
      />
    </>
  );
};

export default ManageAssets;
